<div class="ai-search-root">
    <!--
      Two entry points now. The sidenav link « Assistant Virtuel »
      (NavigationLoaderService.assistantNavItem → AiAssistantService.toggle)
      opens the Assistant tab; the floating bubble and the support
      notification deep-link open the Support tab (SupportStore.requestOpen).
      The host is no longer gated on `isAvailable()`: the assistant is absent
      for service_provider and syndic workspaces, but support is not.
    -->
    @if (panelOpen()) {
      <section
        class="ai-panel"
        [class.ai-panel--fullscreen]="isFullscreen()"
        role="dialog"
        [attr.aria-label]="(activeTab() === 'support' ? 'support.title' : 'aiAssistant.title') | translate"
        (click)="$event.stopPropagation()"
        (keydown)="$event.stopPropagation()">
        <header class="ai-panel__header">
          <!-- The title follows the active tab: a panel headed "Assistant"
               while showing a support conversation says the wrong thing. -->
          <div class="ai-panel__title">
            <mat-icon class="ai-panel__title-icon">
              {{ activeTab() === 'support' ? 'support_agent' : 'auto_awesome' }}
            </mat-icon>
            <span>
              {{ (activeTab() === 'support' ? 'support.title' : 'aiAssistant.title') | translate }}
            </span>
            @if (devUsage(); as usage) {
              <span
                class="ai-panel__usage"
                [matTooltip]="'aiAssistant.quotaDev' | translate">
                {{
                  'aiAssistant.usageDev'
                    | translate: { tokens: usage.tokens, cost: usage.cost }
                }}
              </span>
            }
          </div>
          <div class="ai-panel__actions">
            <button
              type="button"
              mat-icon-button
              [matTooltip]="(isFullscreen() ? 'shared.toolbarAiSearch.exitFullscreen' : 'shared.toolbarAiSearch.fullscreen') | translate"
              (click)="toggleFullscreen()">
              <mat-icon>{{ isFullscreen() ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
            </button>
            <!-- History, temporary mode and "new conversation" belong to the
                 assistant. On the Support tab they would act on a conversation
                 the user is not looking at. -->
            @if (activeTab() === 'assistant' && conversations().length > 0) {
              <button
                type="button"
                mat-icon-button
                [matTooltip]="'shared.toolbarAiSearch.history' | translate"
                [matMenuTriggerFor]="historyMenu"
                #historyTrigger="matMenuTrigger">
                <mat-icon>history</mat-icon>
              </button>
              <!--
                Custom panel content (not mat-menu-item): a nested delete
                button inside mat-menu-item is invalid HTML — browsers close
                the outer button early, so delete wraps under the title and
                the panel collapses to a thin strip. Same pattern as the
                notification bell dropdown.
              -->
              <mat-menu #historyMenu="matMenu" class="ai-history-menu" xPosition="before">
                <div
                  class="ai-history"
                  (click)="$event.stopPropagation()"
                  (keydown.enter)="$any($event).stopPropagation()"
                  tabindex="0">
                  <div class="ai-history__header">
                    <span class="ai-history__heading">
                      {{ 'shared.toolbarAiSearch.history' | translate }}
                    </span>
                  </div>
                  <input
                    class="ai-history__search"
                    type="search"
                    [placeholder]="'aiAssistant.historySearch' | translate"
                    [ngModel]="historyQuery()"
                    (ngModelChange)="historyQuery.set($event)"
                    (click)="$event.stopPropagation()" />
                  <div class="ai-history__list" role="list">
                    @for (c of filteredConversations(); track c.id) {
                      <div
                        class="ai-history__item"
                        [class.ai-history__item--active]="c.id === currentConversationId()"
                        role="listitem">
                        <button
                          type="button"
                          class="ai-history__open"
                          (click)="switchConversation(c.id); historyTrigger.closeMenu()">
                          <mat-icon class="ai-history__leading" aria-hidden="true">
                            {{ c.id === currentConversationId() ? 'chat' : 'chat_bubble_outline' }}
                          </mat-icon>
                          <div class="ai-history__body">
                            <div class="ai-history__title-row">
                              <span class="ai-history__label">{{ conversationLabel(c) }}</span>
                              @if (c.temporary) {
                                <mat-icon
                                  class="ai-history__temp"
                                  [matTooltip]="'shared.toolbarAiSearch.temporary' | translate"
                                  aria-hidden="true">
                                  timer
                                </mat-icon>
                              }
                            </div>
                            @if (c.lastMessageAt) {
                              <div class="ai-history__time">{{ relativeTime(c.lastMessageAt) }}</div>
                            }
                          </div>
                        </button>
                        <button
                          type="button"
                          mat-icon-button
                          class="ai-history__delete"
                          [attr.aria-label]="'shared.toolbarAiSearch.deleteConversation' | translate"
                          [matTooltip]="'shared.toolbarAiSearch.deleteConversation' | translate"
                          (click)="deleteConversation(c.id, $event)">
                          <mat-icon>delete_outline</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                </div>
              </mat-menu>
            }
            @if (activeTab() === 'assistant') {
              <button
                type="button"
                mat-icon-button
                [class.ai-panel__action--active]="temporaryMode()"
                [matTooltip]="(temporaryMode() ? 'shared.toolbarAiSearch.temporaryOn' : 'shared.toolbarAiSearch.makeTemporary') | translate"
                [disabled]="hasMessages()"
                (click)="toggleTemporary()">
                <mat-icon>{{ temporaryMode() ? 'timer' : 'timer_off' }}</mat-icon>
              </button>
              <button
                type="button"
                mat-icon-button
                [matTooltip]="'aiAssistant.newConversation' | translate"
                [disabled]="sending() || !hasMessages()"
                (click)="newConversation()">
                <mat-icon>add_comment</mat-icon>
              </button>
            }
            <button
              type="button"
              mat-icon-button
              [matTooltip]="'aiAssistant.close' | translate"
              (click)="close()">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </header>

        <!--
          Tab strip. Rendered only where BOTH tabs exist: on a workspace with
          no assistant there is nothing to switch between, and a one-item tab
          bar is chrome that says nothing.
        -->
        @if (showTabs()) {
          <div class="ai-panel__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              class="ai-panel__tab"
              [class.ai-panel__tab--active]="activeTab() === 'assistant'"
              [attr.aria-selected]="activeTab() === 'assistant'"
              (click)="selectTab('assistant')">
              <mat-icon>auto_awesome</mat-icon>
              <span>{{ 'aiAssistant.title' | translate }}</span>
            </button>
            <button
              type="button"
              role="tab"
              class="ai-panel__tab"
              [class.ai-panel__tab--active]="activeTab() === 'support'"
              [attr.aria-selected]="activeTab() === 'support'"
              (click)="selectTab('support')">
              <mat-icon>support_agent</mat-icon>
              <span>{{ 'support.title' | translate }}</span>
              @if (supportUnreadCount() > 0) {
                <span class="ai-panel__tab-badge">{{ supportUnreadCount() }}</span>
              }
            </button>
          </div>
        }

        @if (activeTab() === 'assistant') {
        <div
          class="ai-panel__messages"
          #messagesContainer
          [class.ai-panel__messages--drop-target]="isDraggingFiles()"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)">
          @if (isDraggingFiles()) {
            <div class="ai-panel__drop-overlay" aria-hidden="true">
              <mat-icon>attach_file</mat-icon>
              <span>{{ 'aiAssistant.dropHint' | translate }}</span>
            </div>
          }
          @if (!hasMessages()) {
            <div class="ai-panel__empty">
              <mat-icon class="ai-panel__empty-icon">auto_awesome</mat-icon>
              <p class="ai-panel__empty-title">
                {{ 'aiAssistant.empty.title' | translate }}
              </p>
              <p class="ai-panel__empty-hint">
                {{ emptyHintKey() | translate }}
              </p>
              <div class="ai-panel__chips">
                @for (s of suggestions(); track s.id) {
                  <button
                    type="button"
                    class="ai-panel__chip"
                    (click)="useSuggestion(s.message)">
                    <mat-icon>{{ s.icon }}</mat-icon>
                    <span>{{ s.label | translate }}</span>
                  </button>
                }
              </div>
            </div>
          } @else {
            @for (m of visibleMessages(); track m.id) {
              <div
                class="ai-message"
                [class.ai-message--user]="m.role === 'user'"
                [class.ai-message--assistant]="m.role === 'assistant'">
                @if (m.role === 'assistant') {
                  <div class="ai-message__wrap">
                    @if (m.content) {
                      <div class="ai-message__bubble" [innerHTML]="renderMarkdown(m.content)"></div>
                      <button
                        type="button"
                        mat-icon-button
                        class="ai-message__copy"
                        [matTooltip]="(copiedMessageId() === m.id ? 'shared.toolbarAiSearch.copied' : 'shared.toolbarAiSearch.copy') | translate"
                        (click)="copyMessage(m)">
                        <mat-icon>{{ copiedMessageId() === m.id ? 'check' : 'content_copy' }}</mat-icon>
                      </button>
                      @if (feedbackSentIds().has(m.id)) {
                        <span class="ai-message__feedback-thanks" role="status">
                          <mat-icon aria-hidden="true">check</mat-icon>
                          {{ 'aiAssistant.feedback.thanks' | translate }}
                        </span>
                      } @else {
                        <button
                          type="button"
                          mat-icon-button
                          class="ai-message__feedback"
                          [attr.aria-label]="'aiAssistant.feedback.up' | translate"
                          [matTooltip]="'aiAssistant.feedback.up' | translate"
                          (click)="rateMessage(m, 'up')">
                          <mat-icon aria-hidden="true">thumb_up_off_alt</mat-icon>
                        </button>
                        <button
                          type="button"
                          mat-icon-button
                          class="ai-message__feedback"
                          [attr.aria-label]="'aiAssistant.feedback.down' | translate"
                          [matTooltip]="'aiAssistant.feedback.down' | translate"
                          (click)="rateMessage(m, 'down')">
                          <mat-icon aria-hidden="true">thumb_down_off_alt</mat-icon>
                        </button>
                      }
                    }
                  </div>
                  @if (m.toolsUsed && m.toolsUsed.length > 0) {
                    <div class="ai-message__tools">
                      <mat-icon>build</mat-icon>
                      <span>{{ 'shared.toolbarAiSearch.toolsCount' | translate:{ count: m.toolsUsed.length } }}</span>
                      <span class="ai-message__tools-list">{{ toolsLabel(m.toolsUsed) }}</span>
                    </div>
                  }
                } @else {
                  <div class="ai-message__bubble">{{ m.content }}</div>
                }
                @if (m.role === 'assistant' && m.richContent?.length) {
                  <div class="ai-rich-content">
                    @for (block of m.richContent; track trackBlock($index, block)) {
                      <app-ai-block-host
                        [block]="block"
                        (choiceSelected)="onChoiceSelected($event)"
                        (formSubmitted)="onFormSubmitted($event)"
                        (questionAnswered)="onQuestionAnswered($event)"
                        (messageRequested)="onQuestionAnswered($event)"
                        (dataTableRowAction)="onDataTableRowAction($event)">
                      </app-ai-block-host>
                    }
                  </div>
                }
                @if (m.role === 'assistant' && m.pendingActions?.length) {
                  <div class="ai-confirmation-list">
                    @for (action of m.pendingActions; track trackAction($index, action)) {
                      <div
                        class="ai-confirmation-card"
                        [class.ai-confirmation-card--risk-medium]="action.riskLevel === 'medium'"
                        [class.ai-confirmation-card--risk-high]="action.riskLevel === 'high'"
                        [class.ai-confirmation-card--resolved]="action.status !== 'pending'">
                        <div class="ai-confirmation-card__header">
                          <mat-icon class="ai-confirmation-card__icon">
                            {{ action.status === 'approved'
                              ? 'check_circle'
                              : action.status === 'rejected'
                                ? 'cancel'
                                : action.status === 'failed'
                                  ? 'error_outline'
                                  : action.status === 'expired'
                                    ? 'schedule'
                                    : 'pending_actions' }}
                          </mat-icon>
                          <span class="ai-confirmation-card__title">
                            {{ actionTitle(action.toolName) }}
                          </span>
                        </div>
                        <p class="ai-confirmation-card__summary">{{ action.summary }}</p>
                        @if (action.status === 'pending') {
                          <p class="ai-confirmation-card__disclaimer">
                            {{ 'aiAssistant.confirmDisclaimer' | translate }}
                          </p>
                          <div class="ai-confirmation-card__actions">
                            <button
                              type="button"
                              mat-stroked-button
                              [disabled]="isResolvingAction(action.actionId)"
                              (click)="rejectAction(action.actionId)">
                              {{ 'aiAssistant.cancel' | translate }}
                            </button>
                            <button
                              type="button"
                              mat-flat-button
                              color="primary"
                              [disabled]="isResolvingAction(action.actionId)"
                              (click)="confirmAction(action.actionId)">
                              @if (isResolvingAction(action.actionId)) {
                                <mat-spinner diameter="16"></mat-spinner>
                              } @else {
                                {{ 'aiAssistant.confirm' | translate }}
                              }
                            </button>
                          </div>
                        } @else {
                          <p class="ai-confirmation-card__status">
                            {{ ('aiAssistant.statuses.' + action.status) | translate }}
                          </p>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }
            @if (sending() && !isStreamingReply()) {
              <div class="ai-message ai-message--assistant">
                <div class="ai-message__bubble ai-message__bubble--typing">
                  <span class="ai-typing-dot"></span>
                  <span class="ai-typing-dot"></span>
                  <span class="ai-typing-dot"></span>
                  @if (typingStatusKey(); as statusKey) {
                    <span class="ai-typing-label">
                      {{ statusKey | translate }}
                    </span>
                  }
                </div>
              </div>
            }
          }
          <!--
            The way out when the assistant is not helping (#291). Only once
            there is a conversation to hand over — offered on an empty panel it
            would read as "this probably will not work".
          -->
          @if (canHandoff()) {
            <div class="ai-panel__handoff">
              <button type="button" class="ai-panel__handoff-link" (click)="handoffToHuman()">
                <mat-icon>support_agent</mat-icon>
                <span>{{ 'support.handoff.cta' | translate }}</span>
              </button>
            </div>
          }
          <div #messagesEnd></div>
        </div>

        @if (lastError(); as err) {
          <div class="ai-panel__error" role="alert">
            <mat-icon>error_outline</mat-icon>
            <span>{{ 'aiAssistant.errors.generic' | translate }}</span>
            @if (canRetry()) {
              <button
                type="button"
                mat-stroked-button
                class="ai-panel__retry"
                (click)="retry()">
                <mat-icon>refresh</mat-icon>
                {{ 'shared.actions.retry' | translate }}
              </button>
            }
          </div>
        }

        @if (pendingFiles().length > 0 || attachmentError(); as _) {
          <div class="ai-panel__attachments">
            @if (attachmentError(); as err) {
              <div class="ai-panel__attachment-error">
                <mat-icon>error_outline</mat-icon>
                <span>{{ err }}</span>
              </div>
            }
            @if (pendingFiles().length > 0) {
              <div class="ai-panel__chips-row">
                @for (f of pendingFiles(); track f.name; let i = $index) {
                  <div class="ai-panel__file-chip">
                    <mat-icon>{{ fileIcon(f.type) }}</mat-icon>
                    <span class="ai-panel__file-name">{{ f.name }}</span>
                    <span class="ai-panel__file-size">{{ formatFileSize(f.size) }}</span>
                    <button
                      type="button"
                      mat-icon-button
                      class="ai-panel__file-remove"
                      [attr.aria-label]="'shared.toolbarAiSearch.removeFile' | translate:{ name: f.name }"
                      (click)="removePendingFile(i)">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        }

        @if (feedbackDraft(); as fb) {
          <div
            #feedbackSheet
            class="ai-feedback-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-feedback-sheet-title"
            tabindex="-1"
            (keydown.escape)="$event.stopPropagation(); cancelFeedback()">
            <p class="ai-feedback-sheet__title" id="ai-feedback-sheet-title">
              {{ 'aiAssistant.feedback.down' | translate }}
            </p>
            <div class="ai-feedback-sheet__reasons">
              @for (key of feedbackReasonKeys; track key) {
                <button
                  type="button"
                  class="ai-feedback-sheet__chip"
                  [class.ai-feedback-sheet__chip--on]="fb.reasons.includes(key)"
                  (click)="toggleFeedbackReason(key)">
                  {{ ('aiAssistant.feedback.reasons.' + key) | translate }}
                </button>
              }
            </div>
            <textarea
              class="ai-feedback-sheet__comment"
              rows="2"
              [placeholder]="'aiAssistant.feedback.commentPlaceholder' | translate"
              [ngModel]="fb.comment"
              (ngModelChange)="setFeedbackComment($event)"></textarea>
            <div class="ai-feedback-sheet__actions">
              <button type="button" mat-button (click)="cancelFeedback()">
                {{ 'aiAssistant.cancel' | translate }}
              </button>
              <button type="button" mat-flat-button color="primary" (click)="submitFeedbackDown()">
                {{ 'aiAssistant.feedback.submit' | translate }}
              </button>
            </div>
          </div>
        }

        <footer class="ai-panel__composer">
          <input
            #fileInput
            type="file"
            multiple
            hidden
            accept="image/*,application/pdf,text/*,application/json"
            (change)="onFilesPicked($event)" />
          <button
            type="button"
            mat-icon-button
            class="ai-panel__attach"
            [matTooltip]="canAddMoreAttachments() ? ('shared.toolbarAiSearch.attachFiles' | translate:{ max: maxAttachments }) : ('shared.toolbarAiSearch.attachmentLimitReached' | translate)"
            [disabled]="sending() || !canAddMoreAttachments()"
            (click)="fileInput.click()">
            <mat-icon>attach_file</mat-icon>
          </button>
          <textarea
            #panelTextarea
            class="ai-panel__textarea"
            rows="1"
            [placeholder]="inputPlaceholderKey() | translate"
            [disabled]="sending()"
            [ngModel]="draft()"
            (ngModelChange)="draft.set($event)"
            (keydown)="onTextareaKeydown($event)"></textarea>
          <button
            type="button"
            mat-icon-button
            class="ai-panel__send"
            [disabled]="!canSend()"
            [attr.aria-label]="'aiAssistant.send' | translate"
            (click)="send()">
            @if (sending()) {
              <mat-spinner diameter="20"></mat-spinner>
            } @else {
              <mat-icon>send</mat-icon>
            }
          </button>
        </footer>
        }

        @if (activeTab() === 'support') {
          <app-support-panel></app-support-panel>
        }
      </section>
    }
  </div>
